import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Toolbar,
  Typography,
  OutlinedInput,
  Divider,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import { CircularLoader } from 'components/Loader';
import useSidebarState from './hooks/useSidebarState';
import useAccordionFilter from './hooks/useAccordionFilter';
import styles from './sidebar.module.scss';
import Icon from '../../../../components/FormControl/Icon';
import AccordionItem from './sidebarComponent/AccordionItem';
import VideoModal from './sidebarComponent/VideoModal';
import { SSidebarProps } from './types';
import useMenu from 'components/FormControl/Menu/useMenu';
import { useLocation } from 'react-router-dom';
import { SCREEN_OPTIONS } from './constants';
import { TableIconButton } from 'components/Styled';
import { RHelpSection } from 'services/api/endpoints/learnMore';
import useLearnMore from './hooks/useLearnMore';

const Sidebar: React.FC<SSidebarProps> = ({ isOpen, onClose, screenKey }) => {
  const [selectedScreen, setSelectedScreen] = useState(screenKey);
  const { data: helpSectionData, isLoading, fetchData } = useLearnMore();
  const {
    language,
    searchQuery,
    handleChangeLanguage,
    handleSearchInputChange,
    openVideoModal,
    closeVideoModal,
    selectedVideo,
  } = useSidebarState();

  const location = useLocation();

  useEffect(() => {
    handleChangeLanguage('English');
    handleSearchInputChange('');
  }, [location]);

  useEffect(() => {
    setSelectedScreen(screenKey);
    if (screenKey) {
      fetchData(screenKey);
    }
  }, [screenKey]);

  const filteredItems = useAccordionFilter(helpSectionData, language, searchQuery);
  const noMatchesFound = searchQuery.length > 0 && filteredItems.length === 0;

  const {
    MenuComponent: LanguageMenu,
    handleOpen: openLanguageMenu,
    handleClose: closeLanguageMenu,
    open: isLanguageMenuOpen,
  } = useMenu();

  const handleLanguageItemClick = (_: string) => {
    handleChangeLanguage(_ as 'English' | 'Kannada' | 'Hindi' | 'Telugu');
    closeLanguageMenu();
  };

  const {
    MenuComponent: ScreenMenu,
    handleOpen: openScreenMenu,
    handleClose: closeScreenMenu,
    open: isScreenMenuOpen,
  } = useMenu();

  const selectedScreenLabel =
    SCREEN_OPTIONS.find((option) => option.value === selectedScreen)?.label || 'Select Screen';

  // Handle screen change
  const handleScreenChange = (event: SelectChangeEvent<string>) => {
    const newScreen = event.target.value;
    setSelectedScreen(newScreen);
    fetchData(newScreen);
  };

  return (
    <Drawer anchor='right' open={isOpen} onClose={onClose}>
      <Toolbar className={styles.learnMore} variant={'dense'}>
        <div>
          <Typography noWrap component='div' className={styles.learnMoreText}>
            Learn More
          </Typography>
          <Tooltip title={'Select Screen'} placement='right' className={styles.selectScreen}>
            <TableIconButton onClick={(e) => openScreenMenu(e)}>
              <Typography className={styles.selectedScreenLabel}>{selectedScreenLabel}</Typography>
              <Icon icon={'ri-arrow-down-s-fill'} size={'1.7rem'} />
            </TableIconButton>
          </Tooltip>
        </div>

        <div>
          <Tooltip title={'Select Language'} placement='left'>
            <IconButton onClick={(e) => openLanguageMenu(e)}>
              <Typography className={styles.selectLanguage}>{language}</Typography>
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <Icon icon={'ri-close-line'} size='1.5rem' className={styles.closeIcon} />
          </IconButton>
        </div>
      </Toolbar>
      <ScreenMenu
        className={styles.screenMenu}
        menuOptions={SCREEN_OPTIONS.map((option) => option.label)}
        menuTitle={'Select Screen'}
        onItemSelect={(screenLabel) => {
          const selectedScreenOption = SCREEN_OPTIONS.find(
            (option) => option.label === screenLabel,
          );
          if (selectedScreenOption) {
            handleScreenChange({
              target: { value: selectedScreenOption.value },
            } as SelectChangeEvent<string>);
          }
          closeScreenMenu();
        }}
        open={isScreenMenuOpen}
        onClose={closeScreenMenu}
      />
      <LanguageMenu
        menuOptions={['English', 'Kannada', 'Hindi', 'Telugu']}
        menuTitle={'Select Language'}
        onItemSelect={handleLanguageItemClick}
        open={isLanguageMenuOpen}
        onClose={closeLanguageMenu}
      />

      <Toolbar className={styles.searchBar}>
        <OutlinedInput
          className={styles.searchBarText}
          placeholder='Search here'
          value={searchQuery}
          size={'small'}
          onChange={(e) => handleSearchInputChange(e.target.value)}
          startAdornment={
            <Icon icon={'ri-search-line'} size='1.3rem' className={styles.searchIcon} />
          }
          endAdornment={
            <IconButton className={styles.clearButton} onClick={() => handleSearchInputChange('')}>
              <Icon icon={'ri-close-line'} size='1.4rem' className={styles.searchIcon} />
            </IconButton>
          }
        />
      </Toolbar>
      <List className={styles.sideBar}>
        <Divider />

        {isLoading ? (
          <div className={styles.loaderIcon}>
            <CircularLoader size={25} />
          </div>
        ) : noMatchesFound ? (
          <ListItem>
            <ListItemText primary={<span className={styles.noItemText}>No Result Found!</span>} />
          </ListItem>
        ) : filteredItems && filteredItems.length ? (
          filteredItems.map((item: RHelpSection, index: number) => (
            <AccordionItem key={index} item={item} openVideoModal={openVideoModal} index={index} />
          ))
        ) : (
          <ListItem>
            <ListItemText primary={<span className={styles.noItemText}>Coming Soon!!!</span>} />
          </ListItem>
        )}
      </List>
      <VideoModal selectedVideo={selectedVideo} closeVideoModal={closeVideoModal} />
    </Drawer>
  );
};

export default Sidebar;
